// src/pages/admin/AdminAnnouncementList.js
import React, { useEffect, useState } from "react";
import {
  getAllAnnouncements,
  deleteAnnouncement,
} from "../../services/announcementService";
import { useNavigate } from "react-router-dom";
import { fileHref, getImportedAnnouncement, replaceAnnouncementImage } from "../../services/apiClient";
import { captureImportedItemImage } from "../../components/ExcelUploadForm";
import "./AdminAnnouncementList.css";
import { formatDateTime } from "../../utils/dateFormat";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";

// images captured from an Excel import are named "imported-announcement-<importId>.png" — their form data is still
// stored, so the image can be rendered again as a proper A4 page
const importIdOf = (item) => {
  const m = String(item.originalName || item.title || "").match(/^imported-announcement-(\d+)\.png$/);
  return m ? m[1] : null;
};

function AdminAnnouncementList() {
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const paging = usePaging(announcements, "mng_announcements_page_size", "");
  const [redrawing, setRedrawing] = useState(null);
  const [message, setMessage] = useState(null);
  const [version, setVersion] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const [loadError, setLoadError] = useState("");

  async function loadData() {
    setLoading(true);
    setLoadError("");
    try {
      setAnnouncements(await getAllAnnouncements());
    } catch (err) {
      setLoadError(err.message || "โหลดรายการไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    const confirmDelete = window.confirm("ต้องการลบใบประกาศนี้ใช่หรือไม่?");
    if (!confirmDelete) return;

    setMessage(null);
    try {
      await deleteAnnouncement(id);
      setMessage({ ok: true, text: "ลบใบประกาศแล้ว" });
      await loadData();
    } catch (err) {
      setMessage({ ok: false, text: `ลบไม่สำเร็จ: ${err.message}` });
    }
  }

  function handleEdit(id) {
    navigate(`/admin/announcements/${id}/edit`);
  }

  function handleCreate() {
    navigate("/admin/announce");
  }

  // re-render the imported form data and capture it again at A4 size, replacing the old (squeezed) image file
  async function handleRedraw(item) {
    const importId = importIdOf(item);
    if (!importId) return;
    setRedrawing(item.id);
    setMessage(null);
    try {
      const data = await getImportedAnnouncement(importId);
      const imported = data.item || data.data;
      if (!imported) throw new Error("ไม่พบข้อมูลฟอร์มเดิมของประกาศนี้");
      const blob = await captureImportedItemImage(imported);
      await replaceAnnouncementImage(item.id, blob, `imported-announcement-${importId}.png`);
      setVersion((v) => ({ ...v, [item.id]: Date.now() }));
      setMessage({ ok: true, text: `สร้างภาพใหม่ขนาด A4 แล้ว (ประกาศ ${item.id}) · ไฟล์และลิงก์เดิมยังใช้ได้` });
    } catch (err) {
      setMessage({ ok: false, text: err.message || "สร้างภาพใหม่ไม่สำเร็จ" });
    } finally {
      setRedrawing(null);
    }
  }

  if (loading) {
    return <div className="admin-ann-list-container">กำลังโหลด...</div>;
  }

  return (
    <div className="admin-ann-list-container">
      <div className="admin-ann-list-header">
        <h2>จัดการใบประกาศ</h2>
        <button className="btn-primary" onClick={handleCreate}>
          + ลงประกาศใหม่
        </button>
      </div>

      {message && <p className={`adm-msg ${message.ok ? "ok" : "err"}`}>{message.text}</p>}

      {loadError ? (
        <p className="adm-msg err">โหลดรายการไม่สำเร็จ: {loadError} <button type="button" className="btn-primary" onClick={loadData}>ลองอีกครั้ง</button></p>
      ) : announcements.length === 0 ? (
        <div className="empty-state">ยังไม่มีใบประกาศ</div>
      ) : (
        <>
        <PageSummary paging={paging} />
        <table className="admin-ann-table">
          <thead>
            <tr>
              <th>วันที่</th>
              <th>หัวข้อ</th>
              <th>ประเภท</th>
              <th>สถานะ</th>
              <th>ไฟล์/ลิงก์</th>
              <th style={{ width: "140px" }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {paging.shown.map((item) => (
              <tr key={item.id}>
                <td>
                  {item.createdAt
                    ? formatDateTime(item.createdAt)
                    : "-"}
                </td>
                <td>{item.title}</td>
                <td>{item.category || "-"}</td>
                <td>{item.status || "-"}</td>
                <td>
                  {item.fileUrl ? (
                    <a
                      href={`${fileHref(item.fileUrl)}${version[item.id] ? `?v=${version[item.id]}` : ""}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      เปิดไฟล์
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  <button
                    className="btn-ghost"
                    type="button"
                    onClick={() => handleEdit(item.id)}
                  >
                    แก้ไข
                  </button>
                  {importIdOf(item) && (
                    <button
                      className="btn-ghost"
                      type="button"
                      disabled={redrawing !== null}
                      title="สร้างภาพประกาศใหม่จากข้อมูลฟอร์มเดิม ในขนาด A4"
                      onClick={() => handleRedraw(item)}
                    >
                      {redrawing === item.id ? "กำลังสร้าง..." : "สร้างภาพใหม่ (A4)"}
                    </button>
                  )}
                  <button
                    className="btn-danger"
                    type="button"
                    onClick={() => handleDelete(item.id)}
                  >
                    ลบ
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PageNav paging={paging} />
        </>
      )}
    </div>
  );
}

export default AdminAnnouncementList;
